/**
 * Tutorial curriculum (Phase: onboarding) — data-driven lesson definitions.
 *
 * Consumed by src/tutorial/index.js (createTutorial). Pure data + pure
 * helper functions; no DOM / three.js. All copy is Japanese, aimed at
 * first-time players (です・ます調).
 *
 * Shapes
 * ------
 * Lesson:
 *   {
 *     id: string,
 *     title: string,          // レッスン名（UI見出し）
 *     intro: string,          // レッスン冒頭の1〜2文
 *     setup?: { config?, moves? },  // createGame(config) に moves を
 *                             // applyMove で順に適用して初期局面を作る。
 *                             // 省略時は直前の状態を引き継ぐ（卒業レッスン用）
 *     steps: Step[],
 *   }
 *
 * Step:
 *   {
 *     instruction: string | (state) => string,  // 短い指示文
 *     highlight: { cells?, wallSlots?, wallsOnBoard?, forbiddenWallSlots? }
 *               | (state) => highlight,
 *         // UIが光らせる対象。「どこを押せばいいか」を必ず示す。
 *         // forbiddenWallSlots は「赤くなる体験」を促す任意情報（レッスン4）
 *     expect: 正解判定（下記）,
 *     setup?: { config?, moves? },   // このステップだけ局面を作り直す
 *     opponent?: Move,               // 正解後に自動で指されるスクリプト手
 *     success?: string | ({before, after, final, opponentMove}) => string,
 *     hint?: string,                 // wrong（合法だが違う手）のときの文言
 *     illegalMessage?: string,       // 不正手（isLegal=false）のときの文言
 *     blockedMessage?: string,       // 許可外の操作種別のときの文言
 *     allowFreeCamera?: boolean,     // 省略時 true
 *   }
 *
 * expect:
 *   { kind: 'exact',  move: Move }      // 特定の手のみ
 *   { kind: 'oneOf',  moves: Move[] }   // 列挙したどれか
 *   { kind: 'anyMove' }                 // 任意の合法駒移動
 *   { kind: 'anyWall' }                 // 任意の合法壁設置
 *   { kind: 'none' }                    // 入力を受け付けない（卒業画面）
 *
 * すべての setup.moves はコアの applyMove で実際に合法であること
 * （tests/tutorial.test.js が全数検証する）。
 */

import { shortestPathLength, pawnMoves } from '../core/index.js';

// ---- Move shorthands -------------------------------------------------------

const mv = (col, row) => ({ type: 'move', to: { col, row } });
const wl = (col, row, orientation) => ({
  type: 'wall',
  at: { col, row },
  orientation,
});
const wallJump = (col, row) => ({
  type: 'ability',
  ability: 'wallJump',
  to: { col, row },
});
const removeWall = (col, row, orientation) => ({
  type: 'ability',
  ability: 'removeWall',
  at: { col, row },
  orientation,
});

// ---- expect / highlight shorthands ----------------------------------------

const exact = (move) => ({ kind: 'exact', move });
const oneOf = (...moves) => ({ kind: 'oneOf', moves });

const cells = (...list) => ({
  cells: list.map(([col, row]) => ({ col, row })),
});
const wallSlots = (...list) => ({
  wallSlots: list.map(([col, row, orientation]) => ({ col, row, orientation })),
});

/** 壁設置後、相手（後手）の道のりがどう伸びたかを実感させる文言。 */
const wallDistanceMessage = ({ before, after }) => {
  const from = shortestPathLength(before, 1);
  const to = shortestPathLength(after, 1);
  return (
    `壁が置けました！相手のゴールまでの道のりが ` +
    `${from} マスから ${to} マスに伸びました。`
  );
};

// ---- The curriculum --------------------------------------------------------

export const LESSONS = [
  // 1. 移動の基本 -----------------------------------------------------------
  {
    id: 'move-basics',
    title: '移動の基本',
    intro:
      'まずは駒の動かし方から。あなたの駒は手前側にいます。' +
      '目標は、相手側のいちばん奥の列です。',
    setup: { moves: [] },
    steps: [
      {
        instruction: '光っているマスをタップして、駒を1マス前へ進めましょう。',
        highlight: cells([4, 1]),
        expect: exact(mv(4, 1)),
        opponent: mv(4, 7),
        success: 'いいですね！相手も1マス進んできました。',
        hint: '光っているマス（1つ前のマス）をタップしてみましょう。',
      },
      {
        instruction:
          '駒は上下左右に1マスずつ動けます。相手側のいちばん奥の列に' +
          '先に着いたほうが勝ちです。もう1マス前へ進みましょう。',
        highlight: cells([4, 2]),
        expect: exact(mv(4, 2)),
        opponent: mv(4, 6),
        success: 'その調子です。',
      },
      {
        instruction:
          '今度は好きな方向へ動いてみましょう。' +
          '光っているマスならどこでも大丈夫です。',
        highlight: (state) => ({ cells: pawnMoves(state, 0) }),
        expect: { kind: 'anyMove' },
        success: '移動はばっちりですね。',
        hint: '光っているマスのどれかをタップしてみましょう。',
      },
    ],
  },

  // 2. ジャンプ ---------------------------------------------------------------
  {
    id: 'jump',
    title: 'ジャンプ',
    intro: '相手の駒が目の前に来たときは、飛び越えることができます。',
    setup: {
      moves: [
        mv(4, 1), // P0
        mv(4, 7), // P1
        mv(4, 2),
        mv(4, 6),
        mv(4, 3),
        mv(4, 5),
        wl(0, 0, 'h'), // P0: 遠くの隅に壁（局面調整のための待ち手）
        mv(4, 4), // P1: 正面まで接近
      ],
    },
    steps: [
      {
        instruction:
          '相手の駒が正面にいます。光っているマスをタップして、' +
          '相手を飛び越えましょう！',
        highlight: cells([4, 5]),
        expect: exact(mv(4, 5)),
        success:
          'みごとなジャンプ！向かい合ったときは、1手で2マスぶん進めるチャンスです。',
      },
      {
        // まっすぐ飛べないときの斜め回避（この練習だけ局面を作り直す）
        setup: {
          moves: [
            mv(4, 1),
            mv(4, 7),
            mv(4, 2),
            mv(4, 6),
            mv(4, 3),
            mv(4, 5),
            mv(4, 4), // P0: 相手の正面へ
            wl(3, 5, 'h'), // P1: 自分の背後に壁（まっすぐの飛び越え先をふさぐ）
          ],
        },
        instruction:
          '今度は相手のうしろに壁があって、まっすぐ飛び越えられません。' +
          'こんなときは、ななめに回りこめます。光っているマスへ移動しましょう。',
        highlight: cells([3, 5], [5, 5]),
        expect: oneOf(mv(3, 5), mv(5, 5)),
        success:
          '上手です！まっすぐ飛べないときだけ、ななめへ動けるルールです。',
      },
    ],
  },

  // 3. 壁の基本 ---------------------------------------------------------------
  {
    id: 'wall-basics',
    title: '壁の基本',
    intro:
      '壁を置いて、相手の道のりを遠回りにできます。' +
      '壁はマスとマスの間のミゾに置きます。',
    setup: {
      moves: [mv(4, 1), mv(4, 7)],
    },
    steps: [
      {
        instruction:
          '光っているミゾをタップするとプレビューが出ます。' +
          'もう一度タップで設置です。相手の目の前に壁を置いてみましょう。',
        highlight: wallSlots([3, 6, 'h']),
        expect: exact(wl(3, 6, 'h')),
        opponent: mv(5, 7),
        success: wallDistanceMessage,
      },
      {
        instruction:
          '相手は遠回りをはじめました。もう1枚、光っているミゾに壁を置いて、' +
          'さらに遠回りさせましょう。',
        highlight: wallSlots([5, 6, 'h']),
        expect: exact(wl(5, 6, 'h')),
        success: wallDistanceMessage,
      },
    ],
  },

  // 4. 壁のルール -------------------------------------------------------------
  {
    id: 'wall-rule',
    title: '壁のルール',
    intro:
      '壁には大事なルールがひとつ。' +
      '「相手のゴールへの道を完全にふさぐことはできない」です。',
    setup: {
      moves: [
        wl(3, 6, 'h'), // P0: 相手の足元をふさぐ
        mv(4, 7), // P1
        wl(2, 7, 'v'), // P0: 左をふさぐ
        mv(4, 8),
        wl(6, 7, 'v'), // P0: 右をふさぐ（出口は下1か所だけに）
        mv(4, 7),
      ],
    },
    steps: [
      {
        instruction:
          '相手のまわりはほとんど壁で、出口はのこり1本です。' +
          'ためしにその出口のミゾをタップしてみてください。' +
          '赤いプレビューになって置けないはずです。赤は「置けない」の印。' +
          '確認できたら、光っているミゾに壁を置きましょう。',
        highlight: {
          ...wallSlots([5, 5, 'h']),
          forbiddenWallSlots: [{ col: 5, row: 6, orientation: 'h' }],
        },
        expect: exact(wl(5, 5, 'h')),
        illegalMessage:
          'その壁は置けません。壁で相手のゴールへの道を完全にふさぐことは' +
          'できないルールです。赤いプレビューは「置けない」の印。' +
          '光っているミゾに置きましょう。',
        success:
          'これで壁ルールはかんぺきです。道を1本だけ残せば、' +
          '思いきりふさいで大丈夫です。',
        hint: '光っているミゾ（出口のひとつ手前）に置いてみましょう。',
      },
    ],
  },

  // 5. 能力：忍者 ---------------------------------------------------------------
  {
    id: 'ability-ninja',
    title: '能力：忍者',
    intro:
      'キャラクターにはそれぞれ能力があります。' +
      '忍者は1回だけ、壁を飛び越えられます。',
    setup: {
      config: { characters: ['ninja', 'none'] },
      moves: [
        mv(4, 1), // P0（忍者）
        mv(4, 7), // P1
        mv(4, 2),
        wl(4, 2, 'h'), // P1: 忍者の目の前に壁
      ],
    },
    steps: [
      {
        instruction:
          '目の前に壁があります。忍者の能力「壁ジャンプ」で、' +
          '壁の向こうの光っているマスへ飛び越えましょう。',
        highlight: cells([4, 3]),
        expect: exact(wallJump(4, 3)),
        opponent: mv(4, 6),
        success: '壁ジャンプ成功！能力は1回きり。使いどころが勝負です。',
        hint: '能力「壁ジャンプ」を使って、光っているマスを選びましょう。',
      },
      {
        instruction:
          '能力はもう使えません。あとはふつうに進みましょう。光っているマスへ。',
        highlight: cells([4, 4]),
        expect: exact(mv(4, 4)),
        success: '忍者はこれで卒業です。',
      },
    ],
  },

  // 6. 能力：魔術師 -------------------------------------------------------------
  {
    id: 'ability-mage',
    title: '能力：魔術師',
    intro: '魔術師は1回だけ、置かれている壁を取りのぞけます。',
    setup: {
      config: { characters: ['mage', 'none'] },
      moves: [
        mv(4, 1), // P0（魔術師）
        wl(4, 1, 'h'), // P1: 行く手をふさぐ壁
      ],
    },
    steps: [
      {
        instruction:
          '相手の壁が行く手をふさいでいます。' +
          '魔術師の能力で、光っている壁を取りのぞきましょう。',
        highlight: {
          wallsOnBoard: [{ col: 4, row: 1, orientation: 'h' }],
        },
        expect: exact(removeWall(4, 1, 'h')),
        opponent: mv(4, 7),
        success:
          '壁が消えました！取りのぞいた壁は' +
          '持ち主（この壁は相手のもの）の手札には戻らず、そのまま消滅します。',
        hint: '能力「壁の除去」で、光っている壁を選びましょう。',
      },
      {
        instruction: '道が開きました。光っているマスへ進みましょう。',
        highlight: cells([4, 2]),
        expect: exact(mv(4, 2)),
        success: '魔術師もばっちりです。',
      },
    ],
  },

  // 7. ギミック：氷と沼 ----------------------------------------------------------
  {
    id: 'gimmicks',
    title: 'ギミック：氷と沼',
    intro:
      'マップには特別なマスが出ることがあります。氷のマスと沼のマスを体験しましょう。',
    setup: {
      config: {
        gimmicks: [
          { type: 'ice', col: 4, row: 2 },
          { type: 'ice', col: 4, row: 3 },
          { type: 'swamp', col: 4, row: 5 },
        ],
      },
      moves: [mv(4, 1), mv(4, 7)],
    },
    steps: [
      {
        instruction:
          '前のマスは氷です。氷に乗ると、同じ方向へ止まるまで滑ります。' +
          '光っているマスまで一気に進みましょう。',
        highlight: cells([4, 4]),
        expect: exact(mv(4, 4)),
        opponent: mv(4, 6),
        success:
          'ツルーッと一気に滑りました！氷はうまく使えば近道になります。',
      },
      {
        instruction:
          '次のマスは沼です。ためしに入ってみましょう。光っているマスへ。',
        highlight: cells([4, 5]),
        expect: exact(mv(4, 5)),
        opponent: mv(4, 4), // P1: 沼にはまった駒を飛び越えて前進
        success:
          '沼にはまりました…。次のターンは駒を動かせません。' +
          'そのすきに、相手はあなたを飛び越えて先へ行きました。',
      },
      {
        instruction:
          '動けないターンでも、壁は置けます。光っているミゾに壁を置いて、' +
          '相手を遠回りさせてしのぎましょう。',
        highlight: wallSlots([3, 3, 'h']),
        expect: exact(wl(3, 3, 'h')),
        opponent: mv(5, 4),
        success:
          'ナイス対応！沼にはまっても、壁でしっかり時間をかせげました。',
        blockedMessage:
          '沼にはまっている間は駒を動かせません。このターンは壁を置きましょう。',
      },
      {
        instruction:
          '沼の効果が切れました。光っているマスへ進んで脱出しましょう。',
        highlight: cells([4, 6]),
        expect: exact(mv(4, 6)),
        success: 'ギミックも思いのままですね。',
      },
    ],
  },

  // 8. 卒業 -------------------------------------------------------------------
  {
    id: 'graduation',
    title: '卒業',
    intro: 'チュートリアルはここまでです。',
    // setup なし: 直前の盤面を残したまま完了メッセージを表示する
    steps: [
      {
        instruction:
          'おつかれさまでした！移動・ジャンプ・壁・能力・ギミックを' +
          'すべて体験しました。次は「CPU（よわい）」と対局してみましょう！',
        highlight: {},
        expect: { kind: 'none' },
        blockedMessage:
          'チュートリアルは完了しています。CPUとの対局に進みましょう！',
      },
    ],
  },
];
